import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Pin, X } from "lucide-react";
import { TYPE_ORDER, bySheetNumber, sheetTypeOf } from "./sheetType";

const CHIP = { "Floor Plan": "bg-blue-50 text-blue-800", "Enlarged Plan": "bg-violet-50 text-violet-800", "Finish Plan": "bg-emerald-50 text-emerald-800",
               Elevation: "bg-amber-50 text-amber-800", "Section/Detail": "bg-neutral-100 text-neutral-700", Schedule: "bg-pink-50 text-pink-800", Other: "bg-neutral-100 text-neutral-500" };
export const ALL = "All sheets";

export const TypeChip = ({ type }) => <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${CHIP[type] || CHIP.Other}`} data-testid="sheet-type-chip">{type}</span>;

/** DRW-9a — the switcher's groups: one per scope present in the set (a sheet sits in every scope
 *  it belongs to) plus "All sheets"; each group sub-listed by type tag in natural sheet order. */
export const switcherGroups = (sheets) => {
  const live = sheets.filter((s) => s.is_current !== false).map((s) => ({ ...s, type: sheetTypeOf(s.title) })).sort(bySheetNumber);
  const scopes = [...new Set(live.flatMap((s) => s.scopes || []))].sort();
  const byType = (list) => TYPE_ORDER.map((t) => ({ type: t, pages: list.filter((s) => s.type === t) })).filter((x) => x.pages.length);
  return [...scopes.map((sc) => { const pages = live.filter((s) => (s.scopes || []).includes(sc)); return { name: sc, pages, types: byType(pages) }; }),
          { name: ALL, pages: live, types: byType(live) }];
};

/** Prev / Next move by TYPE inside a group: the next sheet of the current sheet's type, natural order. */
export const stepByType = (sheets, groupName, currentId, dir) => {
  const g = switcherGroups(sheets).find((x) => x.name === groupName) || switcherGroups(sheets).slice(-1)[0];
  const cur = g.pages.find((s) => s.id === currentId);
  if (!cur) return null;
  const same = g.pages.filter((s) => s.type === cur.type);
  const i = same.findIndex((s) => s.id === currentId);
  return same[i + dir] || null;
};

/** The group Prev/Next work in by default: the first scope group the sheet belongs to, else All. */
export const homeGroup = (sheets, currentId) => {
  const cur = sheets.find((s) => s.id === currentId);
  return (cur?.scopes || [])[0] || ALL;
};

export default function SheetSwitcher({ sheets, currentId, refs = [], unresolvedRefs = [], onPick, onClose, compact, openScopes = [], activeGroup, onGroupPicked }) {
  const [typeFilter, setTypeFilter] = useState("");
  const groups = useMemo(() => switcherGroups(sheets), [sheets]);
  // opening rule: legend-filtered scopes first and open; otherwise the group holding the current sheet
  const ordered = useMemo(() => {
    const first = openScopes.filter((sc) => groups.some((g) => g.name === sc));
    return [...first.map((sc) => groups.find((g) => g.name === sc)), ...groups.filter((g) => !first.includes(g.name))];
  }, [groups, openScopes]);
  const [open, setOpen] = useState(() => new Set(openScopes.length ? openScopes : [activeGroup || homeGroup(sheets, currentId)]));
  const current = sheets.find((s) => s.id === currentId);
  const isPlan = !!current && sheetTypeOf(current.title) === "Floor Plan";
  // floor plan: the enlarged plans / elevations / details it calls out; any other sheet: every
  // callout it carries. Unresolved targets ride along greyed in both cases.
  const enlarged = useMemo(() => {
    if (!current) return [];
    const seen = new Set();
    const ok = refs.filter((r) => r.sheet_id && r.sheet_id !== currentId && !seen.has(r.sheet_id) && seen.add(r.sheet_id))
      .map((r) => ({ ...r, sheet: sheets.find((s) => s.id === r.sheet_id) }))
      .filter((r) => r.sheet && (!isPlan || ["Enlarged Plan", "Finish Plan", "Elevation", "Section/Detail"].includes(sheetTypeOf(r.sheet.title))))
      .sort((a, b) => bySheetNumber(a.sheet, b.sheet));
    const seenU = new Set();
    const bad = unresolvedRefs.filter((r) => !seenU.has(r.target) && seenU.add(r.target));
    return [...ok, ...bad];
  }, [refs, unresolvedRefs, sheets, current, currentId, isPlan]);
  const pick = (id, groupName) => { onGroupPicked?.(groupName); onPick(id); onClose(); };
  const body = (
    <div className="flex flex-col h-full text-sm" data-testid="sheet-switcher">
      <div className="flex items-center gap-2 px-3 py-2 border-b">
        <span className="font-semibold">Sheets</span>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="h-8 text-xs border rounded-md px-1 ml-1" data-testid="sheet-type-filter">
          <option value="">All types</option>
          {TYPE_ORDER.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button type="button" onClick={onClose} className="ml-auto h-9 w-9 grid place-items-center rounded-md border" aria-label="Close" data-testid="sheet-switcher-close"><X className="w-4 h-4" /></button>
      </div>
      <div className="overflow-auto flex-1">
        {enlarged.length > 0 && (
          <div className="px-3 py-2 border-b bg-violet-50/50" data-testid="enlarged-plans">
            <div className="text-[11px] font-semibold text-violet-900 mb-1">{isPlan ? "Enlarged plans on this sheet" : "Callouts on this sheet"}</div>
            <div className="flex flex-wrap gap-1">
              {enlarged.map((r, i) => r.unresolved
                ? <span key={`u-${i}`} className="h-9 px-2 inline-flex items-center rounded-md border border-dashed text-xs font-mono text-neutral-400 bg-neutral-50" title={`Callout "${r.text}" — ${r.target} is not in this set`} data-testid={`enlarged-unresolved-${r.target}`}>{r.target}</span>
                : <button key={r.sheet_id} type="button" onClick={() => pick(r.sheet_id, activeGroup)} className="h-9 px-2 rounded-md border bg-white text-xs font-mono font-bold" data-testid={`enlarged-${r.sheet.sheet_number}`}>{r.sheet.sheet_number}</button>)}
            </div>
          </div>
        )}
        {ordered.map((g) => {
          const types = g.types.filter((t) => !typeFilter || t.type === typeFilter);
          const n = types.reduce((k, t) => k + t.pages.length, 0);
          if (typeFilter && !n) return null;
          const isOpen = open.has(g.name);
          return (
            <div key={g.name} data-testid={`switcher-group-${g.name}`} data-open={isOpen ? "1" : undefined}>
              <button type="button" onClick={() => setOpen((c) => { const s = new Set(c); s.has(g.name) ? s.delete(g.name) : s.add(g.name); return s; })}
                      className={`w-full flex items-center gap-1 px-3 min-h-10 text-xs font-semibold border-b ${g.name === ALL ? "bg-neutral-100 text-neutral-800" : "bg-neutral-50 text-neutral-700"}`} data-testid={`switcher-toggle-${g.name}`}>
                {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                <span className="truncate">{g.name}</span><span className="ml-auto font-normal text-neutral-500">{n}</span>
              </button>
              {isOpen && types.map((t) => (
                <div key={t.type} data-testid={`switcher-type-${g.name}-${t.type}`}>
                  <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-neutral-500 bg-white border-b flex items-center gap-2"><TypeChip type={t.type} /><span>{t.pages.length}</span></div>
                  {t.pages.map((s) => (
                    <button key={s.id} type="button" onClick={() => pick(s.id, g.name)}
                            className={`w-full flex items-center gap-2 px-3 min-h-11 text-left border-b ${s.id === currentId ? "bg-orange-50 border-l-4 border-l-orange-500" : "hover:bg-neutral-50"}`}
                            data-testid={`switcher-sheet-${g.name}-${s.sheet_number}`} data-current={s.id === currentId ? "1" : undefined} data-type={s.type}>
                      <span className="font-mono font-bold text-sm w-16 shrink-0">{s.sheet_number}</span>
                      <span className="text-xs text-neutral-700 truncate flex-1">{s.title}</span>
                      {s.group_pinned && <Pin className="w-3 h-3 text-orange-700 shrink-0" />}
                      {(s.scopes || []).length > 1 && <span className="text-[10px] text-neutral-500">{s.scopes.length} scopes</span>}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
  if (compact) {
    return (
      <div className="fixed inset-0 z-[60]" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="sheet-switcher-sheet">
        <div className="absolute left-0 right-0 bottom-0 bg-white rounded-t-2xl shadow-2xl h-[70vh] flex flex-col" style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>{body}</div>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-[60] bg-black/30 grid place-items-center p-3" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl w-full max-w-xl h-[75vh] flex flex-col shadow-2xl">{body}</div>
    </div>
  );
}
